/**
 * Component to display media generation errors to users
 * Shows errors from AI image generation, stock search, or existing media search failures
 */
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { AlertCircle, X, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { MediaGenerationError } from '@/types/content.types'

interface MediaGenerationErrorAlertProps {
    errors: MediaGenerationError[]
    onRetry?: () => void
    retrying?: boolean
    onDismiss?: () => void
}

// Map technical step names to user-friendly labels
const STEP_LABELS: Record<string, string> = {
    'AI_GENERATE': 'AI Image Generation',
    'SEARCH_EXISTING': 'Existing Media Search',
    'STOCK_SEARCH': 'Stock Image Search'
}

// Map technical error messages to user-friendly messages
const ERROR_MESSAGE_MAP: Record<string, string> = {
    'KIE task polling timeout': 'Image generation took too long and timed out',
    'Failed to get image URL from KIE task': 'Unable to generate image',
    'All configured steps completed without finding or generating media': 'Could not find or generate suitable media for this content'
}

// Convert technical error message to user-friendly message
function getUserFriendlyErrorMessage(technicalError: string): string {
    // Check for exact matches first
    if (ERROR_MESSAGE_MAP[technicalError]) {
        return ERROR_MESSAGE_MAP[technicalError]
    }

    // Check for partial matches (contains)
    for (const [key, value] of Object.entries(ERROR_MESSAGE_MAP)) {
        if (technicalError.includes(key)) {
            return value
        }
    }

    // Fallback to original message if no mapping found
    return technicalError
}

export function MediaGenerationErrorAlert({
    errors,
    onRetry,
    retrying = false,
    onDismiss
}: MediaGenerationErrorAlertProps) {
    const [isDismissed, setIsDismissed] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    // Don't render if dismissed or no errors
    if (isDismissed || !errors || errors.length === 0) {
        return null
    }

    // Get the most recent error for the main display
    const mostRecentError = errors.reduce((latest, current) => {
        return new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
    }, errors[0])

    const handleDismiss = () => {
        setIsDismissed(true)
        onDismiss?.()
    }

    const hasRetryableErrors = errors.some(error => error.retryable)

    return (
        <Alert variant="destructive" className="relative bg-red-50/80 border-red-200 shadow-md">
            <AlertCircle className="h-5 w-5 text-red-600" />

            {/* Dismiss button */}
            <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="absolute top-2 right-2 h-6 w-6 p-0 hover:bg-red-100"
                aria-label="Dismiss error"
            >
                <X className="h-4 w-4" />
            </Button>

            <div className="pr-8">
                <AlertTitle className="text-red-900 font-semibold mb-2">
                    Media Generation Issue
                </AlertTitle>

                <AlertDescription className="text-red-800 space-y-3">
                    {/* Main error display */}
                    <div className="space-y-1">
                        <div className="flex items-start gap-2">
                            <span className="font-medium text-sm">
                                {STEP_LABELS[mostRecentError.step] || mostRecentError.step}:
                            </span>
                            <span className="text-sm">
                                {getUserFriendlyErrorMessage(mostRecentError.error)}
                            </span>
                        </div>
                        <div className="text-xs text-red-700">
                            {formatDistanceToNow(new Date(mostRecentError.timestamp), { addSuffix: true })}
                        </div>
                    </div>

                    {/* Expandable details if multiple errors or task ID exists */}
                    {(errors.length > 1 || mostRecentError.taskId) && (
                        <div className="border-t border-red-200 pt-2 mt-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="h-6 px-2 text-xs text-red-700 hover:bg-red-100 hover:text-red-900"
                            >
                                {isExpanded ? (
                                    <>
                                        <ChevronUp className="h-3 w-3 mr-1" />
                                        Hide Details
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown className="h-3 w-3 mr-1" />
                                        Show Details {errors.length > 1 && `(${errors.length} errors)`}
                                    </>
                                )}
                            </Button>

                            {isExpanded && (
                                <div className="mt-2 space-y-2 text-xs bg-red-100/50 p-3 rounded-md">
                                    {/* Task ID if available */}
                                    {mostRecentError.taskId && (
                                        <div className="text-red-700">
                                            <span className="font-medium">Task ID:</span>{' '}
                                            <code className="bg-red-200 px-1 py-0.5 rounded text-xs">
                                                {mostRecentError.taskId}
                                            </code>
                                        </div>
                                    )}

                                    {/* Attempts count if available */}
                                    {mostRecentError.attempts && mostRecentError.attempts > 1 && (
                                        <div className="text-red-700">
                                            <span className="font-medium">Attempts:</span>{' '}
                                            {mostRecentError.attempts}
                                        </div>
                                    )}

                                    {/* All errors if multiple */}
                                    {errors.length > 1 && (
                                        <div className="space-y-2 mt-2">
                                            <div className="font-medium text-red-800">All Errors:</div>
                                            {errors.map((error, index) => (
                                                <div key={index} className="pl-2 border-l-2 border-red-300 space-y-1">
                                                    <div className="text-red-900 font-medium">
                                                        {STEP_LABELS[error.step] || error.step}
                                                    </div>
                                                    <div className="text-red-700">
                                                        {getUserFriendlyErrorMessage(error.error)}
                                                    </div>
                                                    {error.requirementIndex !== undefined && (
                                                        <div className="text-red-600 text-xs">
                                                            Media requirement #{error.requirementIndex + 1}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Retry button if errors are retryable */}
                    {hasRetryableErrors && onRetry && (
                        <div className="pt-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onRetry}
                                disabled={retrying}
                                className="bg-white hover:bg-red-50 border-red-300 text-red-700 hover:text-red-800"
                            >
                                <RefreshCw className={`h-3 w-3 mr-2 ${retrying ? 'animate-spin' : ''}`} />
                                {retrying ? 'Retrying...' : 'Retry Media Generation'}
                            </Button>
                        </div>
                    )}
                </AlertDescription>
            </div>
        </Alert>
    )
}
